import React, { useEffect, useState } from "react";
import { Step } from "./MyMultiStepFormBuilder";

export function StepIndicator({
  steps,
  currentStep,
}: {
  steps: Step[];
  currentStep: Step;
}) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      if (typeof window !== "undefined") {
        setIsMobile(window.innerWidth < 640);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // =========================
  // 📱 MOBILE VIEW (progress bar)
  // =========================
  if (isMobile) {
    const progress = ((steps.indexOf(currentStep) + 1) / steps.length) * 100;

    return (
      <div className="w-full max-w-md mx-auto mb-6 px-2">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-500 dark:text-gray-400">
            Step {steps.indexOf(currentStep) + 1} / {steps.length}
          </span>
          <span className="text-blue-600 dark:text-blue-400 font-medium">
            {Math.round(progress)}%
          </span>
        </div>

        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {steps[steps.indexOf(currentStep)]?.label && (
          <div className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
            {steps[steps.indexOf(currentStep)].label}
          </div>
        )}
      </div>
    );
  }

  // =========================
  // 🖥️ DESKTOP VIEW (steps)
  // =========================
  return (
    <div className="w-full max-w-3xl mx-auto mb-10 px-4">
      <div className="flex items-center justify-between">
        {steps.map((step, i) => {
          const isCompleted = i < steps.indexOf(currentStep);
          const isCurrent = i === steps.indexOf(currentStep);

          return (
            <React.Fragment key={step.id}>
              {/* STEP */}
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`
                    w-12 h-12 rounded-full flex items-center justify-center
                    font-semibold transition-all duration-300
                    ${
                      isCompleted
                        ? "bg-green-500 text-white shadow-lg"
                        : isCurrent
                        ? "bg-blue-500 text-white ring-4 ring-blue-200 dark:ring-blue-900"
                        : "bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 text-gray-400"
                    }
                  `}
                >
                  {isCompleted ? (
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    step.icon ?? i + 1
                  )}
                </div>

                {step.label && (
                  <span
                    className={`
                      mt-2 text-sm text-center
                      ${
                        isCompleted
                          ? "text-green-600"
                          : isCurrent
                          ? "text-blue-600 font-semibold"
                          : "text-gray-500"
                      }
                    `}
                  >
                    {step.label}
                  </span>
                )}
              </div>

              {/* CONNECTOR */}
              {i < steps.length - 1 && (
                <div
                  className={`flex-1 h-[2px] mx-2 transition-all duration-300 ${
                    isCompleted
                      ? "bg-green-500"
                      : "bg-gray-300 dark:bg-gray-600"
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}